import type { EditorOverlayLayer } from '@/types/editor';

export type OverlayTemplateCategory = 'Продажи' | 'Соцсети' | 'Контент' | 'События';

export interface OverlayTemplateLayer {
  type?: 'text' | 'sticker';
  text: string;
  x: number;
  y: number;
  size: number;
  fontFamily: string;
  fontWeight: EditorOverlayLayer['fontWeight'];
  fontStyle: EditorOverlayLayer['fontStyle'];
  color: string;
  backgroundColor?: string;
  badgeStyle?: EditorOverlayLayer['badgeStyle'];
  badgeAccentColor?: string;
  badgeBorderColor?: string;
  badgeBorderWidth?: number;
  rotation?: number;
  opacity?: number;
  underline?: boolean;
  offsetStart: number;
  duration: number;
}

export interface OverlayTemplate {
  id: string;
  name: string;
  description: string;
  category: OverlayTemplateCategory;
  /** Used by the picker only; the video itself remains visible after applying. */
  previewBackground: string;
  layers: OverlayTemplateLayer[];
}

/**
 * Production-oriented, editable compositions. Every item is made from real
 * editor layers, so the picker thumbnail, canvas and ffmpeg export all use
 * the same typography, colors, placement and timing.
 */
export const OVERLAY_TEMPLATES: OverlayTemplate[] = [
  {
    id: 'product-drop',
    name: 'Запуск продукта',
    description: 'Анонс, название и CTA',
    category: 'Продажи',
    previewBackground: 'linear-gradient(155deg,#17112c 0%,#0b0712 58%,#3b0764 100%)',
    layers: [
      { type: 'sticker', text: 'НОВИНКА', x: 22, y: 15, size: 62, fontFamily: 'space-grotesk', fontWeight: '800', fontStyle: 'normal', color: '#082f49', backgroundColor: '#67e8f9', badgeAccentColor: '#38bdf8', badgeBorderColor: '#e0f2fe', badgeBorderWidth: 4, badgeStyle: 'pill', offsetStart: 0, duration: 5 },
      { text: 'НОВАЯ\nКОЛЛЕКЦИЯ', x: 50, y: 40, size: 142, fontFamily: 'bebas', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', offsetStart: 0.12, duration: 4.88 },
      { text: 'Создано для вашего ритма', x: 50, y: 61, size: 48, fontFamily: 'dm-sans', fontWeight: '600', fontStyle: 'normal', color: '#c4b5fd', offsetStart: 0.24, duration: 4.76 },
      { type: 'sticker', text: 'СМОТРЕТЬ', x: 50, y: 79, size: 58, fontFamily: 'dm-sans', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', backgroundColor: '#5b35ff', badgeAccentColor: '#9333ea', badgeStyle: 'pill', offsetStart: 0.36, duration: 4.64 },
    ],
  },
  {
    id: 'flash-sale',
    name: 'Большая распродажа',
    description: 'Скидка, дедлайн и кнопка',
    category: 'Продажи',
    previewBackground: 'linear-gradient(145deg,#3f0716 0%,#16050d 55%,#7f1d1d 100%)',
    layers: [
      { text: 'ТОЛЬКО\nСЕГОДНЯ', x: 50, y: 20, size: 88, fontFamily: 'oswald', fontWeight: '700', fontStyle: 'normal', color: '#fecdd3', offsetStart: 0, duration: 4 },
      { type: 'sticker', text: '−40%', x: 50, y: 48, size: 148, fontFamily: 'montserrat', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', backgroundColor: '#e11d48', badgeAccentColor: '#f97316', badgeBorderColor: '#fecdd3', badgeBorderWidth: 6, badgeStyle: 'soft-square', rotation: -4, offsetStart: 0.1, duration: 3.9 },
      { text: 'Успей до 23:59', x: 50, y: 68, size: 52, fontFamily: 'manrope', fontWeight: '700', fontStyle: 'normal', color: '#ffffff', offsetStart: 0.22, duration: 3.78 },
      { type: 'sticker', text: 'КУПИТЬ', x: 50, y: 82, size: 62, fontFamily: 'dm-sans', fontWeight: '800', fontStyle: 'normal', color: '#111827', backgroundColor: '#facc15', badgeAccentColor: '#fb923c', badgeStyle: 'pill', offsetStart: 0.32, duration: 3.68 },
    ],
  },
  {
    id: 'reels-hook',
    name: 'Хук для Reels',
    description: 'Сильный первый экран',
    category: 'Соцсети',
    previewBackground: 'linear-gradient(160deg,#082f49 0%,#07111f 52%,#312e81 100%)',
    layers: [
      { type: 'sticker', text: 'СМОТРИ ДО КОНЦА', x: 50, y: 13, size: 54, fontFamily: 'space-grotesk', fontWeight: '800', fontStyle: 'normal', color: '#082f49', backgroundColor: '#67e8f9', badgeAccentColor: '#a5f3fc', badgeStyle: 'pill', offsetStart: 0, duration: 3.6 },
      { text: '3 ОШИБКИ,\nКОТОРЫЕ ДЕЛАЮТ ВСЕ', x: 50, y: 35, size: 106, fontFamily: 'montserrat', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', offsetStart: 0.08, duration: 3.52 },
      { text: '№2 вас удивит', x: 50, y: 60, size: 58, fontFamily: 'dm-sans', fontWeight: '700', fontStyle: 'normal', color: '#a5f3fc', offsetStart: 0.2, duration: 3.4 },
    ],
  },
  {
    id: 'podcast-quote',
    name: 'Цитата из подкаста',
    description: 'Редакционная подача',
    category: 'Контент',
    previewBackground: 'linear-gradient(150deg,#172554 0%,#080b19 58%,#1e1b4b 100%)',
    layers: [
      { text: '“', x: 20, y: 25, size: 210, fontFamily: 'playfair', fontWeight: '700', fontStyle: 'normal', color: '#818cf8', opacity: 0.8, offsetStart: 0, duration: 6 },
      { text: 'Хорошая идея\nдолжна звучать\nпросто.', x: 50, y: 47, size: 90, fontFamily: 'playfair', fontWeight: '700', fontStyle: 'italic', color: '#ffffff', offsetStart: 0.15, duration: 5.85 },
      { type: 'sticker', text: 'CANNY PODCAST', x: 50, y: 76, size: 48, fontFamily: 'space-grotesk', fontWeight: '800', fontStyle: 'normal', color: '#e0e7ff', backgroundColor: '#3730a3', badgeAccentColor: '#4f46e5', badgeStyle: 'pill', offsetStart: 0.3, duration: 5.7 },
    ],
  },
  {
    id: 'breaking-news',
    name: 'Срочная новость',
    description: 'Новостной lower third',
    category: 'Контент',
    previewBackground: 'linear-gradient(150deg,#111827 0%,#030712 65%,#1f2937 100%)',
    layers: [
      { type: 'sticker', text: 'СРОЧНО', x: 23, y: 72, size: 58, fontFamily: 'oswald', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', backgroundColor: '#dc2626', badgeAccentColor: '#ef4444', badgeStyle: 'soft-square', offsetStart: 0, duration: 5 },
      { text: 'ГЛАВНАЯ НОВОСТЬ ДНЯ', x: 53, y: 82, size: 67, fontFamily: 'oswald', fontWeight: '700', fontStyle: 'normal', color: '#ffffff', offsetStart: 0.08, duration: 4.92 },
      { text: 'Короткое пояснение в одну строку', x: 53, y: 89, size: 39, fontFamily: 'inter', fontWeight: '600', fontStyle: 'normal', color: '#d1d5db', offsetStart: 0.18, duration: 4.82 },
    ],
  },
  {
    id: 'tutorial-steps',
    name: 'Обучение по шагам',
    description: 'Урок или инструкция',
    category: 'Контент',
    previewBackground: 'linear-gradient(155deg,#052e16 0%,#07130d 62%,#134e4a 100%)',
    layers: [
      { type: 'sticker', text: 'ШАГ 01', x: 25, y: 19, size: 62, fontFamily: 'space-grotesk', fontWeight: '800', fontStyle: 'normal', color: '#052e16', backgroundColor: '#86efac', badgeAccentColor: '#2dd4bf', badgeStyle: 'pill', offsetStart: 0, duration: 5 },
      { text: 'ПОДГОТОВЬТЕ\nМАТЕРИАЛЫ', x: 50, y: 43, size: 112, fontFamily: 'bebas', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', offsetStart: 0.12, duration: 4.88 },
      { text: 'Соберите всё необходимое\nперед началом работы', x: 50, y: 65, size: 48, fontFamily: 'dm-sans', fontWeight: '600', fontStyle: 'normal', color: '#bbf7d0', offsetStart: 0.25, duration: 4.75 },
    ],
  },
  {
    id: 'restaurant-offer',
    name: 'Предложение ресторана',
    description: 'Блюдо, цена и CTA',
    category: 'Продажи',
    previewBackground: 'linear-gradient(150deg,#451a03 0%,#160b05 60%,#7c2d12 100%)',
    layers: [
      { type: 'sticker', text: 'ВЫБОР ШЕФА', x: 50, y: 15, size: 52, fontFamily: 'manrope', fontWeight: '800', fontStyle: 'normal', color: '#451a03', backgroundColor: '#fde68a', badgeAccentColor: '#fdba74', badgeStyle: 'pill', offsetStart: 0, duration: 5 },
      { text: 'НОВОЕ\nМЕНЮ', x: 50, y: 40, size: 142, fontFamily: 'playfair', fontWeight: '700', fontStyle: 'italic', color: '#fff7ed', offsetStart: 0.12, duration: 4.88 },
      { text: 'от 3 990 ₸', x: 50, y: 65, size: 68, fontFamily: 'montserrat', fontWeight: '800', fontStyle: 'normal', color: '#fdba74', offsetStart: 0.24, duration: 4.76 },
      { type: 'sticker', text: 'ЗАБРОНИРОВАТЬ', x: 50, y: 80, size: 50, fontFamily: 'dm-sans', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', backgroundColor: '#c2410c', badgeAccentColor: '#ea580c', badgeStyle: 'pill', offsetStart: 0.35, duration: 4.65 },
    ],
  },
  {
    id: 'event-countdown',
    name: 'Анонс события',
    description: 'Дата, событие, место',
    category: 'События',
    previewBackground: 'linear-gradient(140deg,#2e1065 0%,#12091f 55%,#831843 100%)',
    layers: [
      { type: 'sticker', text: '20 ИЮЛЯ · 19:00', x: 50, y: 17, size: 56, fontFamily: 'space-grotesk', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', backgroundColor: '#7c3aed', badgeAccentColor: '#db2777', badgeStyle: 'pill', offsetStart: 0, duration: 6 },
      { text: 'SUMMER\nNIGHT', x: 50, y: 43, size: 154, fontFamily: 'bebas', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', offsetStart: 0.1, duration: 5.9 },
      { text: 'ALMATY · TERRACE 12', x: 50, y: 67, size: 50, fontFamily: 'montserrat', fontWeight: '700', fontStyle: 'normal', color: '#f9a8d4', offsetStart: 0.24, duration: 5.76 },
      { text: 'Билеты уже в продаже', x: 50, y: 77, size: 44, fontFamily: 'dm-sans', fontWeight: '600', fontStyle: 'normal', color: '#ddd6fe', offsetStart: 0.36, duration: 5.64 },
    ],
  },
  {
    id: 'clean-lower-third',
    name: 'Минималистичный титр',
    description: 'Имя и должность',
    category: 'Соцсети',
    previewBackground: 'linear-gradient(150deg,#0f172a 0%,#020617 68%,#1e293b 100%)',
    layers: [
      { text: 'АЛЕКСЕЙ КИМ', x: 31, y: 75, size: 74, fontFamily: 'space-grotesk', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', offsetStart: 0, duration: 5 },
      { type: 'sticker', text: 'CREATIVE DIRECTOR', x: 29, y: 84, size: 39, fontFamily: 'inter', fontWeight: '700', fontStyle: 'normal', color: '#111827', backgroundColor: '#a3e635', badgeAccentColor: '#22c55e', badgeStyle: 'pill', offsetStart: 0.14, duration: 4.86 },
    ],
  },
  {
    id: 'final-cta',
    name: 'Финальный экран',
    description: 'Подписка и призыв',
    category: 'Соцсети',
    previewBackground: 'linear-gradient(155deg,#0f172a 0%,#140e2b 52%,#4c1d95 100%)',
    layers: [
      { text: 'БЫЛО ПОЛЕЗНО?', x: 50, y: 31, size: 104, fontFamily: 'montserrat', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', offsetStart: 0, duration: 4 },
      { text: 'Сохрани, чтобы не потерять', x: 50, y: 46, size: 52, fontFamily: 'dm-sans', fontWeight: '600', fontStyle: 'normal', color: '#c4b5fd', offsetStart: 0.12, duration: 3.88 },
      { type: 'sticker', text: 'ПОДПИСАТЬСЯ', x: 50, y: 65, size: 66, fontFamily: 'space-grotesk', fontWeight: '800', fontStyle: 'normal', color: '#ffffff', backgroundColor: '#5b35ff', badgeAccentColor: '#ec4899', badgeBorderColor: '#ddd6fe', badgeBorderWidth: 4, badgeStyle: 'pill', offsetStart: 0.25, duration: 3.75 },
    ],
  },
];
